import React from 'react'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { useTranslation } from 'react-i18next'
import { KRAJ, jestPolska } from '../../kraj'
import ProductImage from '../ProductImage'
import LogoProducenta from './LogoProducenta'
import StronaDokumentu, { NaglowekSekcji } from './StronaDokumentu'

/**
 * Strona „Zastosowane komponenty".
 *
 * PO CO: klient dostaje ofertę, w której do tej pory sprzęt pojawiał się tylko
 * jako nazwa modelu w schemacie. Nazwa katalogowa („JAM60D41-500/LB") nic mu nie
 * mówi. Tutaj widzi, co dokładnie stanie na jego dachu: zdjęcie, model,
 * najważniejsze parametry, rolę urządzenia i powody, dla których akurat ten.
 *
 * ═══ SKĄD BIORĄ SIĘ ZALETY ═══
 * Dla falownika i magazynu to DANE BADAWCZE — pełne zdania o marce (gwarancja,
 * serwis w Polsce, certyfikaty), pobierane z tabel atrybutów i pokazywane bez
 * przeróbek. Dla modułów PV takiej tabeli nie ma, więc ich zalety są WYLICZONE
 * z doboru tej instalacji i mówią o tym projekcie, a nie o marce
 * (patrz `lib/zaletyKomponentow`).
 *
 * Zalet NIE dopychamy do równej liczby. Komponent bez danych badawczych pokazuje
 * samą rolę i parametry — pusty nagłówek „dlaczego" albo wymyślone zdanie byłyby
 * gorsze niż jego brak na dokumencie, który firma podpisuje własną nazwą.
 */

// Kategorie pokazywane na stronie; etykiety i role leżą w słowniku `projekt`
// (komponenty.kategorie.*, komponenty.rola.*).
const ETYKIETY = { panels: true, inverter: true, battery: true }
const TYP_ZDJECIA = { panels: 'panel', inverter: 'inwerter', battery: 'magazyn' }

/**
 * Zalety przychodzą z lib/zaletyKomponentow jako gotowe polskie zdania.
 *
 * Zdania WYLICZONE przez nas (moduły, gwarancja pojemności magazynu) mają stałą
 * budowę, więc tłumaczymy je po wzorze z liczbami wyjętymi ze zdania. Zdania
 * z DANYCH BADAWCZYCH (falownik, magazyn) to wolny polski tekst o marce, często
 * o polskim serwisie: poza Polską wypadają, bo polskie zdanie albo zdanie
 * o serwisie w Polsce na rumuńskiej ofercie byłoby gorsze niż jego brak.
 * W Polsce każde zdanie spoza wzorów zostaje dokładnie takie, jakie przyszło.
 */
const WZORY_ZALET = {
  moc_modulu: [
    [/^Moc (.+) W z jednego modułu — (.+) szt\. daje (.+) kWp\. Mocniejszy moduł to mniej sztuk na dachu i krótszy montaż\.$/,
      'moc_modulu', ['moc', 'sztuk', 'kwp']],
  ],
  sprawnosc: [
    [/^Sprawność (.+)% — tyle energii słonecznej moduł zamienia na prąd\. Wyższa sprawność to więcej mocy z tej samej powierzchni dachu\.$/,
      'sprawnosc', ['sprawnosc']],
  ],
  gwarancja_modulu: [
    [/^(.+) lat gwarancji producenta na moduł oraz (.+) lat gwarancji na sprawność\.$/,
      'gwarancja_obie', ['produkt', 'sprawnosc']],
    [/^(.+) lat gwarancji producenta na moduł\.$/, 'gwarancja_produkt', ['produkt']],
    [/^(.+) lat gwarancji na sprawność\.$/, 'gwarancja_sprawnosc', ['sprawnosc']],
  ],
  estetyka: [
    [/^Wykonanie full black — jednolita czerń całego modułu, spokojna na dachu\.$/, 'full_black', []],
    [/^Czarna rama — jednolity wygląd całej instalacji\.$/, 'czarna_rama', []],
  ],
  gwarancja_magazynu: [
    [/^Producent gwarantuje co najmniej (.+)% pojemności na koniec okresu gwarancji, a gwarancja obejmuje (.+) cykli ładowania\.$/,
      'magazyn_retencja_cykle', ['retencja', 'cykle']],
    [/^Producent gwarantuje co najmniej (.+)% pojemności na koniec okresu gwarancji\.$/,
      'magazyn_retencja', ['retencja']],
    [/^Gwarancja obejmuje (.+) cykli ładowania\.$/, 'magazyn_cykle', ['cykle']],
  ],
}

/** Liczba cykli z polskiego zapisu („6 000”) w zapisie kraju („6.000”). */
const cykleKraju = (v) => {
  const n = Number(String(v).replace(/\D/g, ''))
  return Number.isFinite(n) && n > 0 ? n.toLocaleString(KRAJ.locale) : v
}

const przetlumaczZalete = (t, zaleta) => {
  const wzor = (WZORY_ZALET[zaleta.pole] || []).find(([re]) => re.test(zaleta.tekst))
  if (!wzor) return jestPolska ? zaleta.tekst : null
  const [re, klucz, nazwy] = wzor
  const dopasowanie = zaleta.tekst.match(re)
  const wyjete = {}
  nazwy.forEach((nazwa, i) => {
    wyjete[nazwa] = nazwa === 'cykle' ? cykleKraju(dopasowanie[i + 1]) : dopasowanie[i + 1]
  })
  return t(`komponenty.zalety.${klucz}`, { ...wyjete, defaultValue: zaleta.tekst })
}

/** Wartość z katalogu (fazy, napięcie, typ, rama) w języku wersji; słownik asystenta. */
const wartosc = (t, v) => t(`asystent:wartosci.${v}`, { defaultValue: String(v) })

/**
 * Parametry warte pokazania klientowi — NAJWYŻEJ CZTERY.
 *
 * Limit jest twardy i celowy: strona ma się zmieścić w jednym arkuszu przy
 * trzech komponentach z sekcjami zalet. Kolejność w listach niżej to kolejność
 * ważności — obcinamy od końca, więc pierwsze pozycje to te, o które klient
 * pyta sam.
 */
const MAX_PARAMETROW = 4

const parametry = (t, pozycja, mocKwp) => {
  const s = pozycja.spec || {}
  const lista = []

  if (pozycja.category === 'panels') {
    if (s.power_w) lista.push(`${s.power_w} W`)
    if (s.frame_colour) lista.push(wartosc(t, s.frame_colour).toLowerCase())
    if (pozycja.quantity > 1) lista.push(t('komponenty.sztuk', { liczba: pozycja.quantity }))
    if (mocKwp) lista.push(t('komponenty.razem_kwp', { kwp: String(mocKwp).replace('.', ',') }))
  }
  if (pozycja.category === 'inverter') {
    if (s.power_kw) lista.push(`${s.power_kw} kW`)
    if (s.phase) lista.push(wartosc(t, s.phase).toLowerCase())
    if (Array.isArray(s.types) && s.types.length) lista.push(s.types.map((v) => wartosc(t, v)).join(', ').toLowerCase())
  }
  if (pozycja.category === 'battery') {
    if (s.capacity_kwh) lista.push(`${String(s.capacity_kwh).replace('.', ',')} kWh`)
    if (s.voltage) lista.push(wartosc(t, s.voltage).toLowerCase())
    if (Array.isArray(s.types) && s.types.length) lista.push(s.types.map((v) => wartosc(t, v)).join(', ').toLowerCase())
  }

  return lista.slice(0, MAX_PARAMETROW)
}

const Zaleta = ({ tekst, doPotwierdzenia }) => {
  const { t } = useTranslation('projekt')
  return (
  <li className="flex items-start gap-2">
    <CheckCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5"
                     style={{ color: 'var(--vv-dok-ciemny)' }} />
    <span className="text-xs text-gray-700 leading-5">
      {tekst}
      {/* Znacznik z danych badawczych zostaje WIDOCZNY. Zdanie niepotwierdzone
          u producenta nadal jest użyteczne, ale klient ma prawo wiedzieć, że nie
          pochodzi wprost od niego. */}
      {doPotwierdzenia && <span className="text-gray-400"> {t('komponenty.do_potwierdzenia')}</span>}
    </span>
  </li>
  )
}

const Pozycja = ({ pozycja, mocKwp, zalety: zaletyPl, tytulZalet }) => {
  const { t } = useTranslation('projekt')
  const chipy = parametry(t, pozycja, mocKwp)
  const zalety = (zaletyPl || [])
    .map((z) => ({ ...z, tekst: przetlumaczZalete(t, z) }))
    .filter((z) => z.tekst)
  const ilosc = pozycja.quantity || 1

  return (
    <div className="flex gap-0 rounded-xl border overflow-hidden break-inside-avoid-page"
         style={{ borderColor: 'var(--vv-dok-obwodka)' }}>
      {/* Pionowy pasek w kolorze firmy — oddziela kolejne komponenty mocniej niż
          sama ramka i prowadzi wzrok w dół strony. */}
      <span className="w-1 flex-shrink-0" style={{ backgroundColor: 'var(--vv-dok)' }} aria-hidden="true" />

      <div className="flex-1 min-w-0 p-4">
        <div className="flex gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wide font-semibold"
               style={{ color: 'var(--vv-dok-ciemny)' }}>
              {ETYKIETY[pozycja.category]
                ? t(`komponenty.kategorie.${pozycja.category}`)
                : pozycja.category}
              {ilosc > 1 && (
                <span className="text-gray-400 font-normal normal-case tracking-normal"> · {t('komponenty.sztuk', { liczba: ilosc })}</span>
              )}
            </p>

            <div className="flex items-start justify-between gap-3 mt-1">
              <p className="text-base font-bold text-gray-900 leading-snug min-w-0">
                {pozycja.model_name}
              </p>
            </div>

            {chipy.length > 0 && (
              <p className="mt-1.5 text-xs font-semibold text-gray-700">
                {chipy.map((c, i) => (
                  <React.Fragment key={c}>
                    {i > 0 && <span className="text-gray-300 font-normal mx-1.5">·</span>}
                    {c}
                  </React.Fragment>
                ))}
              </p>
            )}

            <p className="text-xs text-gray-500 leading-5 mt-2">{t(`komponenty.rola.${pozycja.category}`)}</p>
          </div>

          {/* Zdjęcie po prawej. Białe tło także w ciemnym motywie — zdjęcia
              produktowe są wycinane na biało i na ciemnym mają widoczną obwódkę. */}
          <div className="flex-shrink-0 w-24">
            <div className="vv-arkusz h-24 rounded-lg border border-gray-200 p-1.5">
              <ProductImage
                src={pozycja.img_url} alt=""
                productType={TYP_ZDJECIA[pozycja.category]}
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex justify-center mt-1.5">
              <LogoProducenta slug={pozycja.slug} kategoria={pozycja.category} />
            </div>
          </div>
        </div>

        {zalety.length > 0 && (
          <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--vv-dok-obwodka)' }}>
            <p className="text-[10px] uppercase tracking-wide font-semibold mb-2"
               style={{ color: 'var(--vv-dok-ciemny)' }}>
              {tytulZalet}
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-5 gap-y-1.5">
              {zalety.map((z) => <Zaleta key={z.pole} {...z} />)}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}

const StronaKomponentow = ({
  pozycje, mocKwp = null, zaletyWg = {}, kompatybilnoscPotwierdzona = false,
  nazwaFirmy, numer = 2, zeStron = 5, podtytul = '',
}) => {
  const { t } = useTranslation('projekt')
  const doPokazania = (pozycje || []).filter((p) => ETYKIETY[p.category])
  if (!doPokazania.length) return null

  return (
    <StronaDokumentu numer={numer} zeStron={zeStron} nazwaFirmy={nazwaFirmy} podtytul={podtytul}>
      <NaglowekSekcji
        tytul={t('komponenty.tytul')}
        opis={t('komponenty.opis')}
      />

      <div className="space-y-3">
        {doPokazania.map((p) => (
          <Pozycja
            key={`${p.category}-${p.model_id}`}
            pozycja={p}
            mocKwp={p.category === 'panels' ? mocKwp : null}
            zalety={zaletyWg[p.category]}
            tytulZalet={
              p.category === 'panels'
                ? t('komponenty.dlaczego_modul')
                : t('komponenty.dlaczego', {
                  marka: (zaletyWg[`${p.category}_marka`] || '').toUpperCase() || t('komponenty.ten_sprzet'),
                })
            }
          />
        ))}
      </div>

      {kompatybilnoscPotwierdzona && (
        <div className="mt-4 flex items-center gap-2.5 rounded-xl border border-emerald-200
                        bg-emerald-50 px-4 py-3">
          <CheckCircleIcon className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          <p className="text-sm font-semibold text-emerald-800">
            {t('komponenty.zgodnosc_potwierdzona')}
          </p>
        </div>
      )}
    </StronaDokumentu>
  )
}

export default StronaKomponentow
